import { Injectable, computed, inject, signal } from '@angular/core';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  expand,
  finalize,
  interval,
  map,
  of,
  reduce,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import {
  SUPPORT_API,
  supportShownInThisBrowser,
} from '../support-system.config';
import {
  SupportApiAttachmentReservation,
  SupportApiChat,
  SupportApiMe,
  SupportApiAttachment,
  SupportApiMessage,
  SupportAttachment,
  SupportConversation,
  SupportMessage,
  SupportUploadEvent,
} from '../support-system.models';
import { SupportActivityTrackerService } from './support-activity-tracker.service';
import {
  SupportApiError,
  SupportApiService,
  toSupportError,
} from './support-api.service';
import { SupportSocketService } from './support-socket.service';

const PENDING_PREFIX = 'pending-';

export function newClientId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/** A send that hasn't been saved yet; kept so Retry repeats it exactly. */
interface OutboxItem {
  conversationId: string;
  body: string;
  /** Only for a conversation that doesn't exist on the server yet. */
  target?: string;
  /** Uploaded files; a retry sends the same ids. */
  attachmentIds: string[];
}

/**
 * The client's support chat, backed by `/api/v1/support`.
 *
 * - REST loads and sends everything; the socket only pushes new messages,
 *   take-overs and closes for the chats we joined.
 * - Messages show up right away as 'sending' and are confirmed by the saved
 *   copy (REST answer or socket echo, whichever comes first), matched on
 *   `clientId`. Retries reuse it, so the server never stores a double.
 * - A new conversation has a `pending-…` id until the server creates it.
 * - The backend has no read receipts: what the user has read is remembered
 *   per chat in this browser.
 */
@Injectable({ providedIn: 'root' })
export class SupportChatService {
  private api = inject(SupportApiService);
  private socket = inject(SupportSocketService);
  private tracker = inject(SupportActivityTrackerService);
  private storage = inject(LocalStorageService);

  readonly me = signal<SupportApiMe | null>(null);
  /** The widget is for client accounts; team members get the inbox. */
  readonly available = computed(() => this.me()?.kind === 'client');

  private readonly _conversations = signal<SupportConversation[]>([]);
  readonly conversations = computed(() =>
    [...this._conversations()].sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    ),
  );
  readonly unreadCount = computed(() =>
    this._conversations().reduce((sum, c) => sum + (c.unreadCount || 0), 0),
  );
  readonly loading = signal(false);
  readonly connection = this.socket.state;
  /** False once the backend says uploads aren't configured (503). */
  readonly attachmentsAvailable = signal(true);

  private subscriptions = new Subscription();
  private readonly outbox = new Map<string, OutboxItem>();
  /** pending-… id → server id, so views holding the old id keep working. */
  private readonly aliases = new Map<string, string>();
  /** Chats whose room this socket joined (rooms are lost on reconnect). */
  private readonly joined = new Set<string>();
  private readonly loadingMessages = new Set<string>();
  private openConversationId: string | null = null;
  private meRequest: Subscription | null = null;
  /** In-flight "open chat" per target, so parallel uploads share one call. */
  private readonly opening = new Map<string, Observable<string>>();
  /** Chats with a catch-up request in flight (no overlapping polls). */
  private readonly catchingUp = new Set<string>();
  private lastListSync = 0;

  /** Checks who is logged in and connects. Safe to call repeatedly. */
  init(): void {
    // Hidden unless this browser opted in: no /me call, no socket.
    if (!supportShownInThisBrowser()) return;
    if (this.meRequest) return;
    this.meRequest = this.api.me().subscribe({
      next: (me) => {
        this.meRequest = null;
        this.setMe(me);
      },
      error: () => {
        // 401/403/503 or unreachable: no widget for now.
        this.meRequest = null;
        this.me.set(null);
        this.suspend();
      },
    });
  }

  /** Disconnects but keeps the loaded chats (the shell was left). */
  suspend(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.socket.disconnect();
    this.joined.clear();
  }

  refresh(): void {
    if (!this.available()) return;
    this.loading.set(true);
    this.api.myChats().subscribe({
      next: (page) => {
        this.loading.set(false);
        this.mergeServerChats(page.items || []);
      },
      error: () => this.loading.set(false),
    });
  }

  findConversation(id: string | null): SupportConversation | null {
    if (!id) return null;
    const resolved = this.aliases.get(id) || id;
    return this._conversations().find((c) => c.id === resolved) || null;
  }

  isPending(conversation: SupportConversation | null): boolean {
    return !!conversation && conversation.id.startsWith(PENDING_PREFIX);
  }

  /** The target the server will use: the selected shop if it's the user's. */
  targetFor(selected: string): string | undefined {
    const targets = this.me()?.targets || [];
    return targets.includes(selected) ? selected : targets[0];
  }

  /** The open chat for a target: "Send us a message" continues it. */
  openConversationFor(target: string | undefined): SupportConversation | null {
    return (
      this.conversations().find(
        (c) =>
          c.status === 'open' &&
          c.kind === 'message' &&
          !this.isPending(c) &&
          (!target || c.target === target),
      ) || null
    );
  }

  /** Loads the newest page of a thread, once. */
  loadMessages(id: string): void {
    const conversation = this.findConversation(id);
    if (
      !conversation ||
      conversation.messagesLoaded ||
      this.isPending(conversation) ||
      this.loadingMessages.has(conversation.id)
    ) {
      return;
    }
    this.loadingMessages.add(conversation.id);
    this.api.messages(conversation.id).subscribe({
      next: (page) => {
        this.loadingMessages.delete(conversation.id);
        this.patchConversation(conversation.id, (c) => ({
          ...c,
          messages: mergeMessages(
            c.messages.filter((m) => m.status !== 'sent'),
            page.items.map(fromApiMessage),
          ),
          messagesLoaded: true,
          olderCursor: page.nextCursor,
        }));
        if (this.openConversationId === conversation.id) {
          this.markRead(conversation.id);
        } else {
          this.recountUnread(conversation.id);
        }
      },
      error: () => this.loadingMessages.delete(conversation.id),
    });
  }

  isLoadingMessages(id: string | null): boolean {
    const conversation = this.findConversation(id);
    return !!conversation && this.loadingMessages.has(conversation.id);
  }

  /** "Load earlier messages". */
  loadOlder(id: string): Observable<void> {
    const conversation = this.findConversation(id);
    if (!conversation?.olderCursor) return of(undefined);
    return this.api.messages(conversation.id, conversation.olderCursor).pipe(
      tap((page) =>
        this.patchConversation(conversation.id, (c) => ({
          ...c,
          messages: mergeMessages(c.messages, page.items.map(fromApiMessage)),
          olderCursor: page.nextCursor,
        })),
      ),
      map(() => undefined),
    );
  }

  /**
   * Starts a conversation with its first message. The server returns the
   * open chat for that target if one exists, so this never makes a second.
   */
  startConversation(
    body: string,
    target: string | undefined,
    attachments: SupportAttachment[] = [],
  ): string {
    const clientId = newClientId();
    const conversationId = `${PENDING_PREFIX}${clientId}`;
    const now = new Date().toISOString();
    const message = this.localMessage(clientId, conversationId, body, now, attachments);

    this._conversations.update((list) => [
      {
        id: conversationId,
        kind: 'message',
        status: 'open',
        target: target || '',
        createdAt: now,
        updatedAt: now,
        unreadCount: 0,
        assignedManagerName: null,
        assignedAt: null,
        preview: { body, author: 'user', at: now },
        messages: [message],
        messagesLoaded: true,
        olderCursor: null,
      },
      ...list,
    ]);

    this.outbox.set(clientId, {
      conversationId,
      body,
      target,
      attachmentIds: attachments.map((a) => a.id),
    });
    this.deliver(clientId);
    return conversationId;
  }

  sendMessage(
    conversationId: string,
    body: string,
    attachments: SupportAttachment[] = [],
  ): void {
    const conversation = this.findConversation(conversationId);
    if (
      !conversation ||
      this.isPending(conversation) ||
      conversation.status !== 'open'
    ) {
      return;
    }

    const clientId = newClientId();
    const now = new Date().toISOString();
    this.patchConversation(conversation.id, (c) => ({
      ...c,
      updatedAt: now,
      preview: { body, author: 'user', at: now },
      messages: [
        ...c.messages,
        this.localMessage(clientId, conversation.id, body, now, attachments),
      ],
    }));
    this.outbox.set(clientId, {
      conversationId: conversation.id,
      body,
      attachmentIds: attachments.map((a) => a.id),
    });
    this.deliver(clientId);
  }

  retry(message: SupportMessage): void {
    if (message.status !== 'failed' || !this.outbox.has(message.clientId)) return;
    this.deliver(message.clientId);
  }

  /**
   * The open chat for a target, created on the server if needed. Files can
   * only be uploaded into an existing chat, so the first file of a new
   * conversation creates it.
   */
  ensureConversation(target: string | undefined): Observable<string> {
    const existing = this.openConversationFor(target);
    if (existing) return of(existing.id);

    const key = target || '';
    let request = this.opening.get(key);
    if (!request) {
      request = this.api.openChat(target).pipe(
        tap(({ chat }) => {
          this.opening.delete(key);
          this.applyChat(chat);
          // New: empty history; existing: its messages so far.
          this.loadMessages(chat._id);
        }),
        map(({ chat }) => chat._id),
        catchError((error) => {
          this.opening.delete(key);
          return throwError(() => toSupportError(error));
        }),
        shareReplay(1),
      );
      this.opening.set(key, request);
    }
    return request;
  }

  /** Reserves the file in the chat, then PUTs it straight to storage. */
  uploadAttachment(
    conversationId: string,
    file: File,
  ): Observable<SupportUploadEvent> {
    return this.upload(this.api.reserveAttachment(conversationId, file), file);
  }

  /** A file for an issue report (not tied to a chat). */
  uploadReportAttachment(file: File): Observable<SupportUploadEvent> {
    return this.upload(this.api.reserveIssueReportAttachment(file), file);
  }

  private upload(
    reservation: Observable<SupportApiAttachmentReservation>,
    file: File,
  ): Observable<SupportUploadEvent> {
    return reservation.pipe(
      switchMap(({ attachment, upload }) =>
        this.api.uploadToStorage(upload, file).pipe(
          map(
            (share): SupportUploadEvent =>
              share < 1
                ? { type: 'progress', loaded: share * file.size, total: file.size }
                : { type: 'done', attachment: fromApiAttachment(attachment) },
          ),
        ),
      ),
      catchError((error) => {
        const failure = toSupportError(error);
        if (failure.code === 'UNAVAILABLE') this.attachmentsAvailable.set(false);
        return throwError(() => failure);
      }),
    );
  }

  /** A fresh signed link (valid 5 minutes) to show or open a file. */
  attachmentUrl(conversationId: string, attachmentId: string): Observable<string> {
    const id = this.findConversation(conversationId)?.id || conversationId;
    return this.api.attachmentUrl(id, attachmentId).pipe(map((signed) => signed.url));
  }

  /** The conversation on screen; its incoming messages count as read. */
  setOpenConversation(id: string | null): void {
    this.openConversationId = id ? this.aliases.get(id) || id : null;
    if (this.openConversationId) this.markRead(this.openConversationId);
  }

  markRead(id: string): void {
    const conversation = this.findConversation(id);
    if (!conversation || this.isPending(conversation)) return;
    const latest =
      conversation.messages[conversation.messages.length - 1]?.createdAt ||
      conversation.preview?.at;
    if (latest) this.saveReadAt(conversation.id, latest);
    if (conversation.unreadCount) {
      this.patchConversation(conversation.id, (c) => ({ ...c, unreadCount: 0 }));
    }
  }

  /* ------------------------------ Internals ----------------------------- */

  private setMe(me: SupportApiMe): void {
    const previous = this.me();
    if (previous && previous.id !== me.id) this.reset();
    this.me.set(me);

    if (me.kind !== 'client') {
      this.suspend();
      return;
    }
    this.connect();
    this.refresh();
  }

  /** Someone else signed in without a reload: drop the previous state. */
  private reset(): void {
    this.suspend();
    this.tracker.reset();
    this.outbox.clear();
    this.aliases.clear();
    this.openConversationId = null;
    this._conversations.set([]);
  }

  private connect(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.subscriptions.add(
      this.socket.connected$.subscribe(({ reconnect }) => {
        this.joined.clear();
        this.joinOpenChats();
        if (reconnect) this.catchUp();
      }),
    );
    this.subscriptions.add(
      this.socket.message$.subscribe((m) => this.handleIncoming(m)),
    );
    this.subscriptions.add(
      this.socket.chat$.subscribe((chat) => this.applyChat(chat)),
    );
    // Live events can be missed (lost between backend workers, a socket that
    // is down): REST re-checks what is on screen. Duplicates are merged away.
    this.subscriptions.add(
      interval(SUPPORT_API.threadSyncMs).subscribe(() => this.sync()),
    );
    this.socket.connect();
  }

  /** The open thread every tick, the list every `listSyncMs`. */
  private sync(): void {
    if (document.hidden) return;
    if (Date.now() - this.lastListSync >= SUPPORT_API.listSyncMs) {
      this.lastListSync = Date.now();
      this.refresh();
    }
    const open = this.findConversation(this.openConversationId);
    if (open) this.catchUpChat(open);
  }

  /** Joins the room of every open chat, to hear replies while the panel is closed. */
  private joinOpenChats(): void {
    if (this.connection() !== 'connected') return;
    for (const c of this._conversations()) {
      if (c.status !== 'open' || this.isPending(c) || this.joined.has(c.id)) {
        continue;
      }
      this.joined.add(c.id);
      this.socket.join(c.id).subscribe({
        next: (chat) => this.applyChat(chat),
        error: () => this.joined.delete(c.id),
      });
    }
  }

  /** After a reconnect: reload the list, then fetch what each thread missed. */
  private catchUp(): void {
    this.lastListSync = Date.now();
    this.refresh();
    this._conversations().forEach((c) => this.catchUpChat(c));
  }

  /** Messages saved after the last one we have. */
  private catchUpChat(c: SupportConversation): void {
    const lastSaved = [...c.messages]
      .reverse()
      .find((m) => m.status === 'sent' && m.author !== 'system');
    if (!c.messagesLoaded || !lastSaved || this.isPending(c)) return;
    if (this.catchingUp.has(c.id)) return;
    this.catchingUp.add(c.id);
    this.api
      .messagesAfter(c.id, lastSaved.id)
      .pipe(
        expand((page) =>
          page.hasMore && page.items.length
            ? this.api.messagesAfter(c.id, page.items[page.items.length - 1]._id)
            : EMPTY,
        ),
        reduce((all, page) => [...all, ...page.items], [] as SupportApiMessage[]),
        finalize(() => this.catchingUp.delete(c.id)),
      )
      .subscribe({
        next: (missed) => missed.forEach((m) => this.handleIncoming(m)),
        error: () => {},
      });
  }

  private deliver(clientId: string): void {
    const item = this.outbox.get(clientId);
    if (!item) return;
    this.patchMessage(clientId, { status: 'sending', error: undefined });

    const conversation = this.findConversation(item.conversationId);
    const chatId$: Observable<string> =
      conversation && !this.isPending(conversation)
        ? of(conversation.id)
        : this.api.openChat(item.target).pipe(
            tap(({ chat, created }) =>
              this.adoptChat(item.conversationId, chat, created),
            ),
            map(({ chat }) => chat._id),
          );

    chatId$
      .pipe(
        switchMap((chatId) =>
          this.api.sendMessage(chatId, item.body, clientId, item.attachmentIds),
        ),
        catchError((error) => throwError(() => toSupportError(error))),
      )
      .subscribe({
        next: ({ message }) => {
          this.outbox.delete(clientId);
          this.handleIncoming(message);
        },
        error: (error: SupportApiError) => {
          // The socket echo may already have confirmed it.
          if (this.findMessage(clientId)?.status !== 'sending') return;
          this.patchMessage(clientId, { status: 'failed', error: error.code });
          // Closed meanwhile: show the closed state.
          if (error.code === 'CONFLICT') {
            const id = this.findConversation(item.conversationId)?.id;
            if (id && !id.startsWith(PENDING_PREFIX)) this.reloadChat(id);
          }
        },
      });
  }

  /** A pending conversation now exists on the server. */
  private adoptChat(pendingId: string, chat: SupportApiChat, created: boolean): void {
    const pending = this.findConversation(pendingId);
    if (!pending || !this.isPending(pending)) return;

    this.aliases.set(pendingId, chat._id);
    if (this.openConversationId === pendingId) this.openConversationId = chat._id;

    const existing = this._conversations().find((c) => c.id === chat._id);
    const unsent = pending.messages.map((m) => ({ ...m, conversationId: chat._id }));
    this._conversations.update((list) => {
      const rest = list.filter((c) => c.id !== pendingId && c.id !== chat._id);
      const base = existing || {
        ...fromApiChat(chat),
        // An older chat we didn't have yet: its history is loaded below.
        messagesLoaded: created,
      };
      return [
        {
          ...base,
          ...summaryFields(chat),
          messages: mergeMessages(base.messages, unsent),
        },
        ...rest,
      ];
    });

    this.joinOpenChats();
    if (!created && !existing?.messagesLoaded) this.loadMessages(chat._id);
  }

  private reloadChat(id: string): void {
    this.api.chat(id).subscribe({
      next: (chat) => this.applyChat(chat),
      error: () => {},
    });
  }

  /** A saved message from REST or the socket; replaces its 'sending' copy. */
  private handleIncoming(apiMessage: SupportApiMessage): void {
    const message = fromApiMessage(apiMessage);
    const conversation = this.findConversation(message.conversationId);
    if (!conversation) {
      // A chat we don't know yet (e.g. opened on another device).
      this.refresh();
      return;
    }

    const known = conversation.messages.find(
      (m) => m.id === message.id || m.clientId === message.clientId,
    );
    if (known?.status === 'sent') return;
    if (known) this.outbox.delete(message.clientId);

    const countsAsUnread =
      !known &&
      message.author === 'agent' &&
      this.openConversationId !== conversation.id;
    this.patchConversation(conversation.id, (c) => ({
      ...c,
      updatedAt: maxIso(c.updatedAt, message.createdAt),
      preview:
        !c.preview || message.createdAt >= c.preview.at
          ? { body: message.body, author: message.author, at: message.createdAt }
          : c.preview,
      unreadCount: c.unreadCount + (countsAsUnread ? 1 : 0),
      messages: mergeMessages(c.messages, [message]),
    }));
    if (this.openConversationId === conversation.id) this.markRead(conversation.id);
  }

  /** Summary from a join ack, take-over or close. */
  private applyChat(chat: SupportApiChat): void {
    const known = this.findConversation(chat._id);
    if (!known) {
      this._conversations.update((list) => [fromApiChat(chat), ...list]);
      this.joinOpenChats();
      return;
    }
    this.patchConversation(chat._id, (c) => ({ ...c, ...summaryFields(chat) }));
  }

  /** Server list wins for summaries; loaded messages and pending chats stay. */
  private mergeServerChats(chats: SupportApiChat[]): void {
    const local = this._conversations();
    const merged = chats.map((chat) => {
      const existing = local.find((c) => c.id === chat._id);
      return existing
        ? { ...existing, ...summaryFields(chat) }
        : fromApiChat(chat);
    });
    const pending = local.filter((c) => this.isPending(c));
    this._conversations.set([...pending, ...merged]);

    this.joinOpenChats();
    for (const c of merged) this.recountUnread(c.id);
  }

  /**
   * Unread = team messages after the later of "last read here" and the
   * user's own last message. Needs the messages, so loads them if the
   * latest message is from the team.
   */
  private recountUnread(id: string): void {
    const c = this.findConversation(id);
    if (!c || this.openConversationId === c.id) return;
    const readAt = this.readAt(c.id);
    const settled =
      !c.preview ||
      c.preview.author !== 'agent' ||
      (readAt ? c.preview.at <= readAt : c.status === 'closed');
    // A closed chat never opened in this browser isn't news: no badge.
    if (settled) {
      if (c.unreadCount) this.patchConversation(c.id, (x) => ({ ...x, unreadCount: 0 }));
      return;
    }
    if (!c.messagesLoaded) {
      this.loadMessages(c.id);
      return;
    }
    const lastOwn = [...c.messages].reverse().find((m) => m.author === 'user');
    const since = maxIso(readAt || '', lastOwn?.createdAt || '');
    const unread = c.messages.filter(
      (m) => m.author === 'agent' && m.createdAt > since,
    ).length;
    this.patchConversation(c.id, (x) => ({ ...x, unreadCount: unread }));
  }

  private localMessage(
    clientId: string,
    conversationId: string,
    body: string,
    createdAt: string,
    attachments: SupportAttachment[] = [],
  ): SupportMessage {
    return {
      id: clientId,
      clientId,
      conversationId,
      author: 'user',
      authorName: this.me()?.name,
      body,
      createdAt,
      status: 'sending',
      attachments: attachments.length ? attachments : undefined,
    };
  }

  private findMessage(clientId: string): SupportMessage | null {
    for (const c of this._conversations()) {
      const message = c.messages.find((m) => m.clientId === clientId);
      if (message) return message;
    }
    return null;
  }

  private patchMessage(clientId: string, changes: Partial<SupportMessage>): void {
    this._conversations.update((list) =>
      list.map((c) =>
        c.messages.some((m) => m.clientId === clientId)
          ? {
              ...c,
              messages: c.messages.map((m) =>
                m.clientId === clientId ? { ...m, ...changes } : m,
              ),
            }
          : c,
      ),
    );
  }

  private patchConversation(
    id: string,
    patch: (c: SupportConversation) => SupportConversation,
  ): void {
    this._conversations.update((list) =>
      list.map((c) => (c.id === id ? patch(c) : c)),
    );
  }

  /* Read state: no read receipts on the backend, so it lives in this browser. */

  private readAt(chatId: string): string | null {
    return this.readState()[chatId] || null;
  }

  private saveReadAt(chatId: string, at: string): void {
    const state = this.readState();
    if (state[chatId] && state[chatId] >= at) return;
    this.storage.set(this.readStateKey(), { ...state, [chatId]: at });
  }

  private readState(): Record<string, string> {
    return this.storage.get<Record<string, string>>(this.readStateKey()) || {};
  }

  private readStateKey(): string {
    return `supportReadAt:${this.me()?.id || ''}`;
  }
}

/* ------------------------------ Mapping ------------------------------ */

export function fromApiMessage(m: SupportApiMessage): SupportMessage {
  return {
    id: m._id,
    clientId: m.clientMessageId,
    conversationId: m.chatId,
    author: m.senderType === 'client' ? 'user' : 'agent',
    authorName: m.senderName,
    body: m.body,
    createdAt: m.createdAt,
    status: 'sent',
    attachments: m.attachments?.length
      ? m.attachments.map(fromApiAttachment)
      : undefined,
    ...(m.request ? { request: m.request } : {}),
    // Socket copies never carry it; merging them keeps the REST one.
    ...(m.diagnosticsSummary ? { diagnostics: m.diagnosticsSummary } : {}),
  };
}

export function fromApiAttachment(a: SupportApiAttachment): SupportAttachment {
  return { id: a._id, name: a.name, size: a.size, mimeType: a.mimeType };
}

/** The fields a chat summary always brings up to date. */
function summaryFields(chat: SupportApiChat): Partial<SupportConversation> {
  return {
    status: chat.status,
    target: chat.target,
    updatedAt: chat.lastActivityAt || chat.updatedAt,
    assignedManagerName: chat.assignedManagerName,
    assignedAt: chat.assignedAt,
    preview:
      chat.lastMessageAt && chat.lastMessagePreview !== null
        ? {
            body: chat.lastMessagePreview,
            author: chat.lastMessageSenderType === 'client' ? 'user' : 'agent',
            at: chat.lastMessageAt,
          }
        : null,
  };
}

function fromApiChat(chat: SupportApiChat): SupportConversation {
  return {
    id: chat._id,
    kind: chat.type || 'message',
    createdAt: chat.createdAt,
    unreadCount: 0,
    messages: [],
    messagesLoaded: false,
    olderCursor: null,
    ...(summaryFields(chat) as Required<
      Pick<
        SupportConversation,
        'status' | 'target' | 'updatedAt' | 'assignedManagerName' | 'assignedAt' | 'preview'
      >
    >),
  };
}

/** Union by id/clientId (the later copy wins), ordered by time then id. */
export function mergeMessages(
  current: SupportMessage[],
  incoming: SupportMessage[],
): SupportMessage[] {
  const result = [...current];
  for (const message of incoming) {
    const index = result.findIndex(
      (m) => m.id === message.id || m.clientId === message.clientId,
    );
    if (index === -1) result.push(message);
    else if (result[index].status !== 'sent' || message.status === 'sent') {
      result[index] = {
        ...result[index],
        ...message,
        attachments: keepLocalUrls(result[index].attachments, message.attachments),
      };
    }
  }
  return result.sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
  );
}

/** The saved copy wins, but keeps our in-browser image previews. */
function keepLocalUrls(
  local: SupportAttachment[] | undefined,
  saved: SupportAttachment[] | undefined,
): SupportAttachment[] | undefined {
  if (!saved) return local;
  return saved.map((a) => ({
    ...a,
    localUrl: a.localUrl || local?.find((l) => l.id === a.id)?.localUrl,
  }));
}

function maxIso(a: string, b: string): string {
  return a > b ? a : b;
}
